import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createStepClones, getCloneSourcePreview, getCloneTargetPreview } from "../api";
import { wmErrorMessage } from "../api/wm-client";
import { toast } from "../utils/toast";

/**
 * §Clone-steps: the SOURCE application's cloneable steps that hold data — feeds the "Start another
 * application → Copy from…" picker, which has no target case yet (the real per-step checks run at
 * the creation seam once the agent actually confirms).
 */
export const useCloneSourcePreview = (sourceCaseUid: string | null) =>
  useQuery({
    queryKey: ["nawy-now", "clone-steps", "source-preview", sourceCaseUid],
    queryFn: () => getCloneSourcePreview(sourceCaseUid as string),
    enabled: !!sourceCaseUid,
  });

/**
 * §Clone-steps: one candidate source's preview against an EXISTING target application — feeds
 * "Copy data from another application" on a case that already exists.
 */
export const useCloneTargetPreview = (
  targetCaseUid: string | null,
  sourceCaseUid: string | null
) =>
  useQuery({
    queryKey: ["nawy-now", "clone-steps", "preview", targetCaseUid, sourceCaseUid],
    queryFn: () => getCloneTargetPreview(targetCaseUid as string, sourceCaseUid as string),
    enabled: !!targetCaseUid && !!sourceCaseUid,
  });

export interface CreateStepClonesInput {
  targetCaseUid: string;
  sourceCaseUid: string;
  templateStepIds: number[];
  overwrite?: boolean;
}

/**
 * §Clone-steps: copy steps into an EXISTING application. (Starting a NEW application with a clone
 * goes through `useStartWorkflow`'s `cloneFrom` instead — workflow-manager plans that copy inside
 * the same creation transaction, so there is no separate call for it here.)
 *
 * No success toast: the caller renders a per-step outcome summary from the resolved array (APPLIED /
 * PENDING / CONFLICT / …) — a single "success" toast would flatten a partial result (some steps
 * BLOCKED or CONFLICT while others applied) into something that reads as "all good".
 */
export const useCreateStepClones = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ targetCaseUid, sourceCaseUid, templateStepIds, overwrite }: CreateStepClonesInput) =>
      createStepClones(targetCaseUid, { sourceCaseUid, templateStepIds, overwrite }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["nawy-now"] });
    },
    // A 409 CLONE_OVERWRITE_REQUIRED still lands here — the dialog inspects the same error (via
    // `isCloneOverwriteRequired`) to flip into its confirm-and-resend state, so the agent gets both
    // the server's reason (toasted) and the inline control to act on it.
    onError: (error: unknown) => toast.error(wmErrorMessage(error)),
  });
};
